// "Is my memory actually working?" — a compact live view of the CogniMemo memory
// layer: whether it's connected, how many memories are stored for you, and a feed
// of recent WRITES and READS as the agent uses them. Shown atop the memory view.
import { useCallback, useEffect, useRef, useState } from "react";

import { apiUrl } from "../../apiBase";

type MemEvent = {
  at: string;
  kind: "write" | "read";
  bankId: string;
  detail: string;
  ok: boolean;
  count?: number;
};
type Diag = {
  configured: boolean;
  connection: { ok: boolean; status?: number; error?: string; count?: number };
  storedCount: number;
  totals: { writes: number; writeOk: number; reads: number; readOk: number; readResults: number };
  recent: MemEvent[];
};

const timeAgo = (iso: string): string => {
  const s = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

export function MemoryDiagnostics() {
  const [diag, setDiag] = useState<Diag | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(apiUrl("/api/morrow/memory/diagnostics"), {
        headers: { accept: "application/json" },
      });
      if (res.ok) setDiag((await res.json()) as Diag);
    } catch {
      /* ignore; poll retries */
    }
  }, []);

  useEffect(() => {
    void refresh();
    pollRef.current = setInterval(() => void refresh(), 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refresh]);

  if (!diag) return null;

  const t = diag.totals;
  // Three states: not configured (no key) · configured but the key doesn't work
  // (rejected/unreachable) · actually working.
  const live = diag.configured && diag.connection.ok;
  const statusLabel = !diag.configured
    ? "Memory not configured"
    : diag.connection.ok
      ? "Memory connected"
      : `Memory NOT working — ${diag.connection.error ?? "connection failed"}`;

  return (
    <div className="mb-4 rounded-xl border border-border/70 bg-card/50 p-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px]">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <span className={`size-2 rounded-full ${live ? "bg-emerald-500" : "bg-red-500"}`} />
          {statusLabel}
        </span>
        <span className="text-muted-foreground">
          <strong className="text-foreground">{diag.storedCount}</strong> stored
        </span>
        <span className="text-muted-foreground">
          <strong className="text-foreground">{t.writes}</strong> writes
          {t.writes > 0 ? ` (${t.writeOk} ok)` : ""}
        </span>
        <span className="text-muted-foreground">
          <strong className="text-foreground">{t.reads}</strong> reads
          {t.reads > 0 ? ` → ${t.readResults} results` : ""}
        </span>
      </div>

      {diag.recent.length > 0 ? (
        <div className="mt-3 flex flex-col gap-1 border-t border-border/50 pt-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
            Recent activity
          </p>
          {diag.recent.slice(0, 8).map((e, i) => (
            <div key={`${e.at}-${i}`} className="flex items-center gap-2 text-[12px]">
              <span
                className={`inline-flex w-11 shrink-0 justify-center rounded px-1 py-0.5 text-[10px] font-semibold uppercase ${
                  e.kind === "write"
                    ? "bg-blue-500/10 text-blue-500"
                    : "bg-violet-500/10 text-violet-500"
                }`}
              >
                {e.kind}
              </span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{e.detail}</span>
              {e.kind === "read" ? (
                <span className="shrink-0 text-muted-foreground/70">{e.count ?? 0} found</span>
              ) : (
                <span className={`shrink-0 ${e.ok ? "text-emerald-500" : "text-red-500"}`}>
                  {e.ok ? "saved" : "failed"}
                </span>
              )}
              <span className="w-14 shrink-0 text-right text-muted-foreground/60">
                {timeAgo(e.at)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-[12px] text-muted-foreground/70">
          No memory activity yet this session — ask Morrow something it should remember or recall.
        </p>
      )}
    </div>
  );
}
